import React, { useContext, useMemo } from 'react';
import { encode } from 'html-entities';
import '@wix/ricos/css/ricos-viewer.global.css';
import '@wix/ricos/css/all-plugins-viewer.css';
import '@wix/ricos/css/plugin-audio-viewer.global.css';
import '@wix/ricos/css/plugin-button-viewer.global.css';
import '@wix/ricos/css/plugin-collapsible-list-viewer.global.css';
import '@wix/ricos/css/plugin-divider-viewer.global.css';
import '@wix/ricos/css/plugin-file-upload-viewer.global.css';
import '@wix/ricos/css/plugin-gallery-viewer.global.css';
import '@wix/ricos/css/plugin-giphy-viewer.global.css';
import '@wix/ricos/css/plugin-hashtag-viewer.global.css';
import '@wix/ricos/css/plugin-html-viewer.global.css';
import '@wix/ricos/css/plugin-image-viewer.global.css';
import '@wix/ricos/css/plugin-link-viewer.global.css';
import '@wix/ricos/css/plugin-link-preview-viewer.global.css';
import '@wix/ricos/css/plugin-mentions-viewer.global.css';
import '@wix/ricos/css/plugin-social-polls-viewer.global.css';
import '@wix/ricos/css/plugin-spoiler-viewer.global.css';
import '@wix/ricos/css/plugin-table-viewer.global.css';
import '@wix/ricos/css/plugin-vertical-embed-viewer.global.css';
import '@wix/ricos/css/plugin-video-viewer.global.css';

import {
  RicosViewer,
  ensureRicosContent,
  ensureDraftContent,
  fromDraft,
  toDraft,
  pluginCodeBlockViewer,
  pluginHashtagViewer,
  pluginIndentViewer,
  pluginLineSpacingViewer,
  pluginLinkViewer,
  pluginMentionsViewer,
  pluginSpoilerViewer,
  pluginTextColorViewer,
  pluginTextHighlightViewer,
  pluginEmojiViewer,
  pluginVideoViewer,
  pluginAudioViewer,
} from '@wix/ricos';
import {
  pluginActionButtonLazy,
  pluginCollapsibleListLazy,
  pluginDividerLazy,
  pluginFileUploadLazy,
  pluginGalleryLazy,
  pluginGiphyLazy,
  pluginHtmlLazy,
  pluginImageLazy,
  pluginLinkButtonLazy,
  pluginLinkPreviewLazy,
  pluginPollLazy,
  pluginTableLazy,
  pluginVerticalEmbedLazy,
} from '@wix/ricos/lazy';

import { DraftJSObject } from '../../../../types/RichContent';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { AppContext } from '../SettingsProvider/SettingsProvider';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import settingsParams from '../../settingsParams';
import { st, classes } from '../QuestionsWrapper/styles.st.css';
import stylesParams from '../../stylesParams';
import { useLayoutType } from '../../../../hooks/useLayoutType';
import { getDomain, getMediaFileType } from './getMediaFileType';

enum PlatformType {
  Livesite = 'Livesite',
  Editor = 'Editor',
}
const plugins = [
  pluginActionButtonLazy(),
  pluginAudioViewer({
    getAudioUrl: (src: any) => `https://static.wixstatic.com/${src.id}`,
  }),
  pluginCodeBlockViewer(),
  pluginCollapsibleListLazy(),
  pluginDividerLazy(),
  pluginEmojiViewer(),
  pluginGalleryLazy({
    disableExpand: true,
    scrollingElement: () => document.querySelector('[data-hook="expandIcon"]'),
  }),
  pluginGiphyLazy(),
  pluginHashtagViewer(),
  pluginHtmlLazy({
    width: '100%',
    minWidth: '100%',
    siteDomain: 'https://nivina3.wixsite.com/my-site-334',
  }),
  pluginImageLazy({ disableTransition: true }),
  pluginIndentViewer(),
  pluginLineSpacingViewer(),
  pluginLinkButtonLazy(),
  pluginLinkPreviewLazy(),
  pluginLinkViewer(),
  pluginMentionsViewer(),
  pluginPollLazy(),
  pluginSpoilerViewer(),
  pluginTableLazy(),
  pluginTextColorViewer(),
  pluginTextHighlightViewer(),
  pluginVerticalEmbedLazy(),
  pluginVideoViewer(),
];
const idContainsPrefix = (id: string) => {
  return id.includes('/');
};
interface ResolveFileUrlProps {
  id: string;
  type: string;
  privacy: string;
  url: string;
  name: string;
}

const resolveFileUrl =
  (metaSiteId: string) =>
  ({
    id,
    type,
    privacy,
    url,
    name,
  }: {
    id: string;
    type: string;
    privacy: string;
    url: string;
    name: string;
  }) => {
    if (privacy !== 'private' && idContainsPrefix(id) && metaSiteId) {
      try {
        const domain = getDomain(getMediaFileType(type), metaSiteId);
        return Promise.resolve(
          `https://${domain}/${id}?dn=${encodeURIComponent(name)}`,
        );
      } catch (error) {
        // Fall back to original URL if domain resolution fails
        return Promise.resolve(url);
      }
    }
    return Promise.resolve(url);
  };

const RCV = ({
  convertAnswerToJSON,
}: {
  convertAnswerToJSON: DraftJSObject | string;
}) => {
  const {
    isEditor3,
    questionId,
    appAPI: { metaSiteId } = {},
    instance,
  } = useContext(AppContext);
  const environment = useEnvironment();
  const { isMobile } = environment;
  const settings = useSettings();
  const styles = useStyles();
  const answerFont = styles.get(stylesParams.answerTextFont);
  const answerTextColor = styles.get(stylesParams.answerTextColor);
  const answersLinksColor = styles.get(stylesParams.answersLinksColor);
  const customStyles = useMemo(() => {
    const elements = [
      'p',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'span',
      'link',
      'hashtag',
      'quote',
      'audio',
    ];
    const customAnswerStyle = {
      fontFamily: answerFont.family,
      fontSize: `${answerFont.size}px`,
      ...(answerFont.style?.bold && { fontWeight: 'bold' }),
      ...(answerFont.style?.italic && { fontStyle: 'italic' }),
      ...(answerFont.style?.underline && { textDecoration: 'underline' }),
    };

    const result = elements.reduce((acc: { [key: string]: any }, element) => {
      acc[element] = customAnswerStyle;
      return acc;
    }, {});
    return result;
  }, [answerFont]);

  const {
    layout: { layoutValue: layout },
  } = useLayoutType(isEditor3);
  const validContent =
    typeof convertAnswerToJSON === 'string'
      ? encode(convertAnswerToJSON)
      : toDraft(
          fromDraft(ensureDraftContent(convertAnswerToJSON), {
            ignoreUnsupportedValues: true,
          }),
        );
  const textAlignment = settings.get(settingsParams.textAlignment);
  const direction = textAlignment === 'right' ? 'rtl' : 'ltr';
  const parentClass = st(
    classes.answerWrapper,
    { layout },
    questionId ? `RCV${questionId}` : undefined,
  );
  const content = ensureRicosContent(validContent as DraftJSObject);
  return (
    <div
      style={{ direction }}
      className={st(classes.answer, { layout, mobile: isMobile })}
    >
      {typeof convertAnswerToJSON === 'string' ? (
        convertAnswerToJSON
      ) : (
        <RicosViewer
          biSettings={{
            consumer: '[wix-labs] faq-ooi',
            usage: 'answer',
            platform: environment.isEditor
              ? PlatformType.Editor
              : PlatformType.Livesite,
            postId: questionId,
            containerId: metaSiteId || questionId || 'faq-ooi-answer',
          }}
          instance={instance}
          theme={{
            parentClass,
            customStyles: customStyles ?? {},
          }}
          textAlignment={textAlignment}
          plugins={[
            pluginFileUploadLazy({
              resolveFileUrl: (src: ResolveFileUrlProps) => {
                if (!metaSiteId) {
                  return Promise.resolve(src.url);
                }
                return resolveFileUrl(metaSiteId)(src);
              },
              downloadTarget: '_blank',
            }),
            ...plugins,
          ]}
          content={content}
        />
      )}
    </div>
  );
};
RCV.displayName = 'FaqOoiRCVAnswer';
export default RCV;
